import baseURL from '../Components/url';

export const DEFAULT_THEME_COLOR = '#F80050';

export const THEME_PRESETS = [
    { name: 'Fucsia Original', color: '#F80050' },
    { name: 'Azul Corporativo', color: '#0d6efd' },
    { name: 'Verde Éxito', color: '#198754' },
    { name: 'Púrpura Elegante', color: '#752FB5' },
    { name: 'Naranja Cálido', color: '#fd7e14' },
    { name: 'Rojo Escarlata', color: '#dc3545' },
    { name: 'Turquesa Marino', color: '#00b4d8' },
    { name: 'Negro Grafito', color: '#212529' },
];

const CONFIG_STORAGE_KEY = 'system_config';

export const getDefaultConfig = () => ({
    nombreNegocio: 'Restaurante & Tienda',
    whatsappContacto: '',
    whatsappPedidos: '',
    themeColor: DEFAULT_THEME_COLOR,
    sonidoPedidoQR: 'ring_1.mp3',
    sonidoPedidoMesero: 'ring_2.mp3',
    sonidoLlamadaMesero: 'beep.mp3',
    usarPrecios: 'si',
});

/**
 * Obtiene la configuración actual almacenada en caché local (localStorage)
 */
export const getConfig = () => {
    try {
        const stored = localStorage.getItem(CONFIG_STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            return {
                ...getDefaultConfig(),
                ...parsed,
            };
        }
    } catch (e) {
        console.error('Error al leer configuración de localStorage:', e);
    }
    return getDefaultConfig();
};

/**
 * Aplica el color del tema modificando las variables CSS en el root del documento.
 */
export const applyTheme = (color) => {
    const themeColor = color || DEFAULT_THEME_COLOR;
    if (typeof document !== 'undefined') {
        document.documentElement.style.setProperty('--color1', themeColor);
        document.documentElement.style.setProperty('--loading', `${themeColor}60`);
    }
};

/**
 * Consulta la configuración guardada en la base de datos a través de configuracionGet.php
 * y sincroniza el tema y el estado local.
 */
export const fetchConfigFromDB = async () => {
    try {
        const response = await fetch(`${baseURL}/configuracionGet.php`, {
            method: 'GET',
        });
        if (response.ok) {
            const data = await response.json();
            if (data && data.configuracion) {
                const conf = {
                    nombreNegocio: data.configuracion.nombreNegocio || 'Restaurante & Tienda',
                    whatsappContacto: data.configuracion.whatsappContacto || '',
                    whatsappPedidos: data.configuracion.whatsappPedidos || '',
                    themeColor: data.configuracion.themeColor || DEFAULT_THEME_COLOR,
                    sonidoPedidoQR: data.configuracion.sonidoPedidoQR || 'ring_1.mp3',
                    sonidoPedidoMesero: data.configuracion.sonidoPedidoMesero || 'ring_2.mp3',
                    sonidoLlamadaMesero: data.configuracion.sonidoLlamadaMesero || 'beep.mp3',
                    usarPrecios: data.configuracion.usarPrecios || 'si',
                };
                try {
                    localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(conf));
                } catch (err) {
                    console.error(err);
                }
                applyTheme(conf.themeColor);
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('configUpdated', { detail: conf }));
                }
                return conf;
            }
        }
    } catch (error) {
        console.warn('No se pudo conectar con configuracionGet.php, usando caché local:', error);
    }
    return getConfig();
};

/**
 * Inicializa el tema y configuración
 */
export const initTheme = () => {
    const local = getConfig();
    applyTheme(local.themeColor);
    fetchConfigFromDB();
    return local;
};

/**
 * Guarda la configuración en la base de datos MySQL (configuracionPut.php)
 * y actualiza el tema, sonidos y caché local.
 */
export const saveConfigToDB = async (newConfig) => {
    const current = getConfig();
    const payload = {
        nombreNegocio: newConfig.nombreNegocio !== undefined ? newConfig.nombreNegocio : current.nombreNegocio,
        whatsappContacto: newConfig.whatsappContacto !== undefined ? newConfig.whatsappContacto : current.whatsappContacto,
        whatsappPedidos: newConfig.whatsappPedidos !== undefined ? newConfig.whatsappPedidos : current.whatsappPedidos,
        themeColor: newConfig.themeColor || current.themeColor || DEFAULT_THEME_COLOR,
        sonidoPedidoQR: newConfig.sonidoPedidoQR || current.sonidoPedidoQR || 'ring_1.mp3',
        sonidoPedidoMesero: newConfig.sonidoPedidoMesero || current.sonidoPedidoMesero || 'ring_2.mp3',
        sonidoLlamadaMesero: newConfig.sonidoLlamadaMesero || current.sonidoLlamadaMesero || 'beep.mp3',
        usarPrecios: newConfig.usarPrecios !== undefined ? newConfig.usarPrecios : (current.usarPrecios || 'si'),
    };

    let dbResponse = null;
    try {
        const response = await fetch(`${baseURL}/configuracionPut.php`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });
        dbResponse = await response.json();
    } catch (error) {
        console.error('Error al guardar en base de datos:', error);
    }

    try {
        localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
        console.error('Error al guardar configuración en localStorage:', e);
    }

    applyTheme(payload.themeColor);

    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('configUpdated', { detail: payload }));
    }

    return { localConfig: payload, dbResponse };
};

/**
 * Restaura la configuración a los valores por defecto
 */
export const resetConfig = async () => {
    return await saveConfigToDB(getDefaultConfig());
};

/**
 * Obtiene el número de teléfono del contacto general
 */
export const fetchDefaultContactPhone = async () => {
    try {
        const res = await fetch(`${baseURL}/contactoGet.php`);
        const data = await res.json();
        if (data && data.contacto && data.contacto.length > 0) {
            const ultimo = data.contacto[data.contacto.length - 1];
            return ultimo.telefono || '';
        }
    } catch (e) {
        console.error('Error al obtener teléfono de contacto:', e);
    }
    return '';
};
